import { contains, pluck } from 'ramda';
import React, { FC, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router';
import { MainSource } from '../../../constants/Source';
import { mapIndexed } from '../../../utils/array';
import { useMMGRSettings } from '../../../utils/hoc/withMMGRSettings';
import { useTrackSearchAndFiltersAnalytics } from '../../../utils/hooks/bi/useTrackSearchAndFiltersAnalytics';
import { ClickOrigins } from '../../../utils/hooks/bi/useTrackSearchAndFiltersAnalytics/useTrackSearchAndFiltersAnalytics.types';
import { isRecommended } from '../../../utils/media-manager-sdk/sources';
import { RouteBuilder } from '../../../utils/RouteHelpers';
import { Tag, Text } from '../../common';
import { HorizontalSection } from '../../common/HorizontalSection';
import { Section } from './Section';
import styles from './SectionsLayout.scss';
import { IOuter } from './SectionsLayout.types';

export const SectionsLayout: FC<IOuter> = ({
  source,
  dataHook,
  sections,
  rowsInSection,
  isItemSelected,
  onItemClick,
  onShowAllClick,
  beginDrag,
  alwaysShowAction,
  onDropOnFolder,
  onFolderDoubleClick,
  onFileDoubleClick,
  openExpandedView,
  onItemRightClick,
  selectedItems,
  buyItem,
  currentFolder,
  searchQuery,
}) => {
  const { mmgrSettings } = useMMGRSettings();
  const { isSecureContext: isSecure } = mmgrSettings;

  const history = useHistory();
  const { trackSearchAndFiltersShowAllClick } = useTrackSearchAndFiltersAnalytics();

  const handleQuickSearchClick = useCallback(
    (sourceName) => {
      history.push(RouteBuilder.Search(sourceName, searchQuery));

      trackSearchAndFiltersShowAllClick({
        target: sourceName,
        sectionsAmount: sections.length,
        sectionsArray: JSON.stringify(pluck('sectionName', sections)),
        clickOrigin: ClickOrigins.QuickSearch,
      });
    },
    [sections, searchQuery],
  );

  const handleSiteFilesClick = useCallback(
    () => handleQuickSearchClick(MainSource.SiteFiles),
    [handleQuickSearchClick],
  );
  const handleMediaFromWixClick = useCallback(
    () => handleQuickSearchClick(MainSource.MediaFromWix),
    [handleQuickSearchClick],
  );
  const handleUnsplashClick = useCallback(() => handleQuickSearchClick(MainSource.Unsplash), [handleQuickSearchClick]);
  const handleShutterstockClick = useCallback(
    () => handleQuickSearchClick(MainSource.Shutterstock),
    [handleQuickSearchClick],
  );

  const { formatMessage } = useIntl();

  const quickSearchLabel = formatMessage({ id: 'files-manager.quick-search' });
  const siteFilesLabel = formatMessage({ id: 'categories.site-files' });
  const mediaFromWixLabel = formatMessage({ id: 'categories.free-from-wix' });
  const unsplashLabel = formatMessage({ id: 'categories.unsplash' });
  const shutterstockLabel = formatMessage({ id: 'categories.shutterstock' });

  const shouldShowQuickSearch = !isRecommended(source);

  const searchResultsSources = shouldShowQuickSearch ? pluck('sectionName')(sections) : [];

  const shouldShowSiteFilesQuickSearch = shouldShowQuickSearch && contains(MainSource.SiteFiles, searchResultsSources);
  const shouldShowMediaFromWixQuickSearch =
    shouldShowQuickSearch && contains(MainSource.MediaFromWix, searchResultsSources);
  const shouldShowShutterstockQuickSearch =
    shouldShowQuickSearch && contains(MainSource.Shutterstock, searchResultsSources);
  const shouldShowUnsplashQuickSearch = shouldShowQuickSearch && contains(MainSource.Unsplash, searchResultsSources);

  return (
    <div className={styles.wrapper} data-hook={dataHook}>
      {shouldShowQuickSearch && (
        <HorizontalSection
          header={
            <Text weight="bold" size="medium">
              {quickSearchLabel}
            </Text>
          }
          className={styles.firstNewSection}
          dataHook="quick-search"
        >
          {shouldShowSiteFilesQuickSearch && (
            <Tag
              id="siteFiles"
              onClick={handleSiteFilesClick}
              size="small"
              removable={false}
              className={styles.quickSearchTag}
              dataHook="quick-search-site-files"
            >
              {siteFilesLabel}
            </Tag>
          )}
          {shouldShowMediaFromWixQuickSearch && (
            <Tag
              id="mediaFromWix"
              onClick={handleMediaFromWixClick}
              size="small"
              removable={false}
              className={styles.quickSearchTag}
            >
              {mediaFromWixLabel}
            </Tag>
          )}

          {shouldShowUnsplashQuickSearch && (
            <Tag
              id="unsplash"
              onClick={handleUnsplashClick}
              size="small"
              removable={false}
              className={styles.quickSearchTag}
            >
              {unsplashLabel}
            </Tag>
          )}

          {shouldShowShutterstockQuickSearch && (
            <Tag
              id="shutterstock"
              onClick={handleShutterstockClick}
              size="small"
              removable={false}
              className={styles.quickSearchTag}
            >
              {shutterstockLabel}
            </Tag>
          )}
        </HorizontalSection>
      )}

      <div className={styles.sections}>
        {mapIndexed(
          ({ sectionName, title, data, action, subCategoryId }, index) => (
            <Section
              beginDrag={beginDrag}
              isSecure={isSecure}
              isItemSelected={isItemSelected}
              selectedItems={selectedItems}
              onItemClick={onItemClick}
              onItemRightClick={onItemRightClick}
              sectionName={sectionName}
              key={index}
              title={title}
              items={data}
              action={action}
              maxRowsToShow={rowsInSection}
              onShowAllClick={onShowAllClick}
              onDropOnFolder={onDropOnFolder}
              onFileDoubleClick={onFileDoubleClick}
              onFolderDoubleClick={onFolderDoubleClick}
              alwaysShowAction={alwaysShowAction}
              openExpandedView={openExpandedView}
              buyItem={buyItem}
              currentFolder={currentFolder}
              sectionClassName={styles.section}
              subCategoryId={subCategoryId}
              sectionIndex={index}
            />
          ),
          sections,
        )}
      </div>
    </div>
  );
};
